import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Switch, Table, Tooltip, Typography, message,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import config from '../../../config';
import { getCompanyWhatsAppSenderUsers, setCompanyWhatsAppSenderUser } from '../api';
import {
  SenderUser, apiError, plural, unwrap,
} from './shared';

const { Text } = Typography;

const ROLE_LABELS: Record<string, string> = {
  [config.userRoles.ghAdmin]: 'GoHire Admin',
  [config.userRoles.companyAdmin]: 'Company Admin',
  [config.userRoles.user]: 'User',
};

const ON_NUMBER_HELP = 'Users on the number see unassigned WhatsApp conversations and get new-message notifications. '
  + 'Taking someone off returns their assigned WhatsApp conversations to the team queue.';

interface Props {
  companyId: string;
  botmapId: string;
  /** Called after a user is put on / taken off, so the card can refresh counts and history. */
  onChanged?: () => void;
}

/** Who is on the company's WhatsApp number (ADR-037). */
const SenderUsersTable: React.FC<Props> = ({ companyId, botmapId, onChanged }) => {
  const [users, setUsers] = useState<SenderUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = unwrap(await getCompanyWhatsAppSenderUsers(companyId, botmapId));
      setUsers(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setUsers([]);
      setError(apiError(err, 'Failed to load the users on this number.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, botmapId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (user: SenderUser, enabled: boolean) => {
    setSaving((s) => ({ ...s, [user.id]: true }));
    setUsers((rows) => rows.map((u) => (u.id === user.id ? { ...u, onSender: enabled } : u)));
    try {
      const res = unwrap(await setCompanyWhatsAppSenderUser(companyId, botmapId, user.id, enabled)) || {};
      const released = Number(res.conversationsReleased || 0);
      if (enabled) {
        message.success(`${user.name} put back on the number`);
      } else {
        message.success(`${user.name} taken off the number${released > 0
          ? ` · ${plural(released, 'conversation')} returned to the queue` : ''}`);
      }
      if (onChanged) onChanged();
    } catch (err) {
      setUsers((rows) => rows.map((u) => (u.id === user.id ? { ...u, onSender: !enabled } : u)));
      message.error(apiError(err, 'Failed to update the user.'));
    } finally {
      setSaving((s) => ({ ...s, [user.id]: false }));
    }
  };

  const onCount = users.filter((u) => u.onSender).length;

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      render: (email: string) => <Text type="secondary">{email}</Text>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => ROLE_LABELS[role] || 'User',
    },
    {
      title: (
        <Tooltip title={ON_NUMBER_HELP}>
          <span style={{ whiteSpace: 'nowrap' }}>
            On number
            {' '}
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </span>
        </Tooltip>
      ),
      key: 'onSender',
      align: 'right' as const,
      width: 120,
      render: (_: unknown, user: SenderUser) => (
        <Switch
          size="small"
          checked={user.onSender}
          loading={!!saving[user.id]}
          onChange={(checked) => handleToggle(user, checked)}
          data-testid={`company-whatsapp-user-switch-${user.id}`}
          aria-label={`${user.name} on the WhatsApp number`}
        />
      ),
    },
  ];

  return (
    <div data-testid="company-whatsapp-users">
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8,
      }}
      >
        <Text strong>Users on this number</Text>
        {!loading && !error && (
          <Text type="secondary" style={{ fontSize: 13 }} data-testid="company-whatsapp-users-count">
            {onCount} of {plural(users.length, 'user')}
          </Text>
        )}
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={users}
        loading={loading}
        pagination={users.length > 10 ? { pageSize: 10, size: 'small', showSizeChanger: false } : false}
        locale={{ emptyText: 'No active users in this company' }}
      />
    </div>
  );
};

export default SenderUsersTable;
